import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import * as Colors from "@mui/material/colors";
import { addAnswer, removeAnswer } from "../../actions/answerEditorActions";
import {updateGroupTemplate} from "../../actions/groupEditorActions";
import {updateQuestionTemplate} from "../../actions/questionEditorActions";
import AnswerEditor from "../qd/answers/AnswerEditorHook";
import ArgoTextFieldSelectHook from "./ArgoTextFieldSelectHook";
import ArgoButtonHook from "./ArgoButtonHook";
import ArgoTextFieldHook from "./ArgoTextFieldHook";
import ArgoCheckboxHook from "./ArgoCheckboxHook";
import ArgoTextDisplayCustomHook from "./ArgoTextDisplayCustomHook";
import ArgoColorInputHook from "./ArgoColorInputHook";
import * as icon from "../../constants/iconConstants";
import * as answerTypes from "../../constants/answerTypeConstants";
import * as color from "../../constants/colorConstants";
import * as portalShared from "../../portal/shared/metadataConstantsAndUtilities"
import * as TEMPLATE from "../../constants/templateConstants";
import * as metadata from "../../utilities/metadata";
import * as groupType from "../../constants/groupTypeLikertOrDefault";

const ArgoLikertAnswerGeneratorHook = (props) => {
    const {
        fileName,
        templateType,
        template,
        groupTemplateMode = false,
        isLikert = false,
        displayAsLikert,
        answerOptionTemplates = [],
        questionDepth = 1,
        setMetadataErrorText,
        customMetadataKey,
        templateUpdateFunction,
        createMode
    } = props;

    const dispatch = useDispatch();

    const getInitialMetadataTag = () => {
        return templateType === TEMPLATE.TYPE.GROUP ?
            portalShared.CUSTOM_DDO_GRP_FORMAT :
            portalShared.CUSTOM_DDO_QUE_FORMAT;
    };

    const getInitialTextEntryTypes = () => {
        if (templateType === TEMPLATE.TYPE.GROUP) {
            return {
                likertTitles: TEMPLATE.TEXT_TYPE.GROUP.LIKERT_TITLES,
                responsiveLabels: TEMPLATE.TEXT_TYPE.GROUP.RESPONSIVE_LABELS
            };
        }
        return {
            likertTitles: TEMPLATE.TEXT_TYPE.QUESTION.LIKERT_TITLES,
            responsiveLabels: TEMPLATE.TEXT_TYPE.QUESTION.RESPONSIVE_LABELS
        };
    };

    const textEntryTypes = getInitialTextEntryTypes();
    const metadataTag = getInitialMetadataTag();

    // Use a merged state helper to emulate class setState semantics for this very large legacy component
    const [state, _setState] = useState({
        answersError: null,
        toolbarReviewButtonDisabled: false,
        metadataErrorText: null,
        likertErrorText: null,
        descDisplayMetadataValue: "",
        descDisplayMetadataValueObj: null,
        likertAddAnswerTemplate: "2",
        likertAddAnswerTemplateFormat: "radios",
        likertMode: icon.LIKERT_ICON_MODE_DEFAULT,
        likertAnswerTextDisplay: icon.LIKERT_TITLES_ALL,
        likertResponsiveLabels: icon.LIKERT_ICON_LABELS_NO_TEXT,
        likertResponsiveLabelsInit: false,
        metadataTagCustomTemplate: metadataTag,
        textEntryTypeLikertTitles: textEntryTypes.likertTitles,
        textEntryTypeResponsiveLabels: textEntryTypes.responsiveLabels,
        likertObj: portalShared.getCustomLikertObject(templateType, [textEntryTypes.likertTitles, textEntryTypes.responsiveLabels]),
    });

    const setState = useCallback((updates, cb) => {
        _setState(prev => {
            const newState = {
                ...prev,
                ...(typeof updates === 'function' ? updates(prev) : updates)
            };
            if (cb) {
                // Defer callback to next tick similar to class setState
                setTimeout(() => cb(newState), 0);
            }
            return newState;
        });
    }, []);

    const setStateCallback = useCallback((stateObj) => {
        setState(stateObj);
    }, [setState]);

    const pageInit = useCallback((currProps) => {
        if (!currProps?.template?.metadata) {
            return;
        }

        setState(prevState => {
            const tempLikert = portalShared.cloneObj(prevState.likertObj);
            const updates = {};
            const templateMetadata = currProps.template.metadata || {};

            if (templateMetadata.hasOwnProperty(currProps.customMetadataKey)) {
                const obj = JSON.parse(templateMetadata[currProps.customMetadataKey]);

                if (obj.hasOwnProperty("version") && obj.likertObj) {
                    tempLikert.alignAnswersRight = obj.likertObj.alignAnswersRight;
                    tempLikert.alignQuestionsAndAnswers = obj.likertObj.alignQuestionsAndAnswers;
                    tempLikert.titleType = obj.likertObj.titleType;
                    tempLikert.titleLeft = obj.likertObj.titleLeft;
                    tempLikert.titleCenter = obj.likertObj.titleCenter;
                    tempLikert.titleRight = obj.likertObj.titleRight;
                    tempLikert.titlesCustomTextObj = obj.likertObj.titlesCustomTextObj;
                    tempLikert.responsiveLabelsCustomTextObj = obj.likertObj.responsiveLabelsCustomTextObj;

                    if (!prevState.likertResponsiveLabelsInit) {
                        updates.likertResponsiveLabelsInit = true;
                        updates.likertResponsiveLabels = obj.likertObj.responsiveLabels;
                        updates.likertObj = portalShared.cloneObj(obj.likertObj);
                        return updates;
                    }
                } else {
                    // LEGACY FIX
                    tempLikert.alignAnswersRight = obj.alignAnswersRight;
                    tempLikert.alignQuestionsAndAnswers = obj.alignQuestionsAndAnswers;
                    tempLikert.titleType = obj.titleType;
                    tempLikert.titleLeft = obj.titleLeft;
                    tempLikert.titleCenter = obj.titleCenter;
                    tempLikert.titleRight = obj.titleRight;
                    tempLikert.titlesCustomTextObj.format = "default";
                    tempLikert.responsiveLabelsCustomTextObj.format = "default";
                }
            }

            updates.likertObj = tempLikert;
            return updates;
        });
    }, [setState]);

    // Handle componentWillReceiveProps logic with useEffect
    useEffect(() => {
        if (!template) {
            return;
        }

        pageInit({template, customMetadataKey});

        if (groupTemplateMode) {

            if (isLikert) {
                if (!template.metadata?.hasOwnProperty(portalShared.CUSTOM_DDO_GRP_FORMAT)) {
                    let obj = portalShared.getCustomGroupObj();
                    let updatedTemplate = {...template};
                    let metadata = {...updatedTemplate.metadata};
                    metadata[portalShared.CUSTOM_DDO_GRP_FORMAT] = JSON.stringify(obj);
                    updatedTemplate.metadata = metadata;
                    updatedTemplate.questionGroupType = groupType.GROUP_TYPE_LIKERT; // ZZZZZ Override to FIX bug created in Class to Hook AI conversion
                    dispatch(updateGroupTemplate(updatedTemplate));
                }
            } else if (template.metadata?.hasOwnProperty(portalShared.CUSTOM_DDO_GRP_FORMAT)) {
                const updatedTemplate = {...template};
                const tempMetadata = {...updatedTemplate.metadata};
                delete tempMetadata[portalShared.CUSTOM_DDO_GRP_FORMAT];
                deleteMetadata(tempMetadata);
            }
        } else if (isLikert) {
            if (!template.metadata?.hasOwnProperty(portalShared.CUSTOM_DDO_QUE_FORMAT)) {
                const obj = portalShared.getCustomQuestionSingleSelectObj();
                const updatedTemplate = {...template};
                const metadata = {...updatedTemplate.metadata};
                metadata[portalShared.CUSTOM_DDO_QUE_FORMAT] = JSON.stringify(obj);
                updatedTemplate.metadata = metadata;
                dispatch(updateQuestionTemplate(updatedTemplate));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, customMetadataKey, groupTemplateMode, isLikert, dispatch, pageInit]);

    const handleMetadataChange = useCallback((metadataObj) => {

        if (!templateUpdateFunction) return; // safeguard
        let updatedTemplate = {...template};
        updatedTemplate.metadata = metadataObj;

        templateUpdateFunction(updatedTemplate);
    }, [templateUpdateFunction, template]);

    // const checkForErrorsOnScreen = useCallback((compProps) => {
    //
    //     //const isValidating = props.options.isValidating;
    //     let errorText = {};
    //
    //     //let answersInvalid = false;
    //     //let answersRequired = false;
    //
    //     // props.options.validate.forEach(template => {
    //     //     if (template.name.toUpperCase() === RemoveExtraWhitespace(name).toUpperCase() && template.templateKey.toUpperCase() !== templateKey.toUpperCase()) {
    //     //         nameExists = true;
    //     //     }
    //     //     if (template.templateKey.toUpperCase() === templateKey.toUpperCase() && props.options.isEditCreate) {
    //     //         keyExists = true;
    //     //     }
    //     // });
    //
    //
    //
    //     // Validate answers only if this is a Likert Group
    //     if (isLikert && !validateAnswers(compProps.answerOptionTemplates)) {
    //         errorText["answersError"] = "Answer name is required.";
    //         //answersInvalid = true;
    //     }
    //
    //
    //     if (isLikert && props.answerOptionTemplates.length === 0) {
    //         errorText["likertErrorText"] = "At least one answer is required.";
    //         //answersRequired = true;
    //     }
    //     else {
    //         errorText["likertErrorText"] = " ";
    //     }
    //
    // }, [isLikert]);

    const onButtonClickAddLikertAnswers = useCallback(() => {

        // if (state.likertAddAnswerTemplateFormat === "squares") {
        //     this.setMetadata(portalShared.DISPLAY_DEF_RADIO_AS_BUTTON_GROUP, "");
        // }
        //
        // if (state.likertAddAnswerTemplateFormat === "circles") {
        //     this.setMetadata(portalShared.DISPLAY_DEF_BUTTON_AS_CIRCLE, "");
        // }

        let iconType = "";
        //let iconMode = ""; // Fill, No Title
        let iconAnswerTextDisplay = "";
        let iconResponsiveLabel = "";
        //let answerOptionText = "";
        //let iconColor = "";

        let answersToAdd = parseInt(state.likertAddAnswerTemplate, 10);
        for (let i = 0; i < answersToAdd; i++) {

            switch (state.likertAddAnswerTemplateFormat) {
                case "radios":

                    if (state.likertResponsiveLabels === icon.RESPONSIVE_LABELS_YES_TEXT) {
                        dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)).toString(), index: i, metadata: {[icon.ICON_POSITION]: iconAnswerTextDisplay, [icon.ICON_TYPE]: icon.TYPE_RADIO, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [portalShared.USE_LIKERT_RESPONSIVE_LABEL]: icon.getLabel(answersToAdd, i)}}, questionDepth));
                    }
                    else {
                        dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)).toString(), index: i, metadata: {[icon.ICON_POSITION]: iconAnswerTextDisplay, [icon.ICON_TYPE]: icon.TYPE_RADIO, [icon.ICON_TITLES]: state.likertAnswerTextDisplay}}, questionDepth));
                    }

                    break;
                case "thumbs":

                    iconAnswerTextDisplay = (state.likertAnswerTextDisplay === icon.LIKERT_TITLES_HIDDEN) ? icon.POSITION_ICON_NO_TITLE : icon.POSITION_ICON;

                    switch (i) {
                        case 0:
                            iconType = icon.TYPE_THUMBS_UP;
                            dispatch(addAnswer(groupTemplateMode, {optionText: "Yes", index: i, metadata: {[icon.ICON_POSITION]: iconAnswerTextDisplay, [icon.ICON_TYPE]: iconType, [icon.ICON_TITLES]: state.likertAnswerTextDisplay}}));
                            break;
                        case 1:
                            iconType = icon.TYPE_THUMBS_DOWN;
                            dispatch(addAnswer(groupTemplateMode, {optionText: "No", index: i, metadata: {[icon.ICON_POSITION]: iconAnswerTextDisplay, [icon.ICON_TYPE]: iconType, [icon.ICON_TITLES]: state.likertAnswerTextDisplay}}));
                            break;
                        // no default
                    }
                    break;
                case "stars":

                    switch (state.likertAnswerTextDisplay) {
                        case icon.LIKERT_TITLES_ALL:
                            iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL : icon.POSITION_ICON;
                            break;
                        case icon.LIKERT_TITLES_LR:
                            if (i === 0 || i === (answersToAdd - 1)) {
                                iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL : icon.POSITION_ICON;
                            } else {
                                iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL_NO_TITLE : icon.POSITION_ICON_NO_TITLE;
                            }
                            break;
                        case icon.LIKERT_TITLES_LCR:
                            if (i === 0 || i === ((answersToAdd - 1)/2) || i === (answersToAdd - 1)) {
                                iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL : icon.POSITION_ICON;
                            } else {
                                iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL_NO_TITLE : icon.POSITION_ICON_NO_TITLE;
                            }
                            break;
                        case icon.LIKERT_TITLES_HIDDEN:
                            iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL_NO_TITLE : icon.POSITION_ICON_NO_TITLE;
                            break;
                        // no default
                    }

                    if (state.likertResponsiveLabels === icon.RESPONSIVE_LABELS_YES_TEXT) {
                        dispatch(addAnswer(groupTemplateMode, {optionText: (i+1).toString(), index: i, metadata: {[icon.ICON_POSITION]: iconAnswerTextDisplay, [icon.ICON_TYPE]: icon.TYPE_STAR, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.USE_LIKERT_RESPONSIVE_LABEL]: icon.getLabel(answersToAdd, i)}}));
                    }
                    else {
                        dispatch(addAnswer(groupTemplateMode, {optionText: (i+1).toString(), index: i, metadata: {[icon.ICON_POSITION]: iconAnswerTextDisplay, [icon.ICON_TYPE]: icon.TYPE_STAR, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX}}));
                    }
                    break;
                case "facesA":
                case "facesD":

                    if (answersToAdd === 4) {
                        if (state.likertAddAnswerTemplateFormat === "facesA") {

                            switch (i) {
                                case 0:
                                    iconType = icon.TYPE_SENTIMENT_VERY_DISSATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                case 1:
                                    iconType = icon.TYPE_SENTIMENT_DISSATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                case 2:
                                    iconType = icon.TYPE_SENTIMENT_SATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                case 3:
                                    iconType = icon.TYPE_SENTIMENT_VERY_SATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                // no default
                            }

                        } else {

                            switch (i) {
                                case 0:
                                    iconType = icon.TYPE_SENTIMENT_VERY_SATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, 3);
                                    break;
                                case 1:
                                    iconType = icon.TYPE_SENTIMENT_SATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, 2);
                                    break;
                                case 2:
                                    iconType = icon.TYPE_SENTIMENT_DISSATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, 1);
                                    break;
                                case 3:
                                    iconType = icon.TYPE_SENTIMENT_VERY_DISSATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, 0);
                                    break;
                                // no default
                            }

                        }
                    }
                    else {
                        if (state.likertAddAnswerTemplateFormat === "facesA") {

                            switch (i) {
                                case 0:
                                    iconType = icon.TYPE_SENTIMENT_VERY_DISSATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                case 1:
                                    iconType = (answersToAdd === 3) ? icon.TYPE_SENTIMENT_NEUTRAL : icon.TYPE_SENTIMENT_DISSATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                case 2:
                                    iconType = (answersToAdd === 3) ? icon.TYPE_SENTIMENT_VERY_SATISFIED : icon.TYPE_SENTIMENT_NEUTRAL;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                case 3:
                                    iconType = icon.TYPE_SENTIMENT_SATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                case 4:
                                    iconType = icon.TYPE_SENTIMENT_VERY_SATISFIED;
                                    iconResponsiveLabel = icon.getLabel(answersToAdd, i);
                                    break;
                                // no default
                            }

                        } else {

                            switch (i) {
                                case 0:
                                    iconType = icon.TYPE_SENTIMENT_VERY_SATISFIED;
                                    iconResponsiveLabel = icon.getLabel(5, (answersToAdd === 3) ? 3 : 4);
                                    break;
                                case 1:
                                    iconType = (answersToAdd === 3) ? icon.TYPE_SENTIMENT_NEUTRAL : icon.TYPE_SENTIMENT_SATISFIED;
                                    iconResponsiveLabel = icon.getLabel(5, (answersToAdd === 3) ? 2 : 3);
                                    break;
                                case 2:
                                    iconType = (answersToAdd === 3) ? icon.TYPE_SENTIMENT_VERY_DISSATISFIED : icon.TYPE_SENTIMENT_NEUTRAL;
                                    iconResponsiveLabel = icon.getLabel(5, (answersToAdd === 3) ? 1 : 2);
                                    break;
                                case 3:
                                    iconType = icon.TYPE_SENTIMENT_DISSATISFIED;
                                    iconResponsiveLabel = icon.getLabel(5, 1);
                                    break;
                                case 4:
                                    iconType = icon.TYPE_SENTIMENT_VERY_DISSATISFIED;
                                    iconResponsiveLabel = icon.getLabel(5, 0);
                                    break;
                                // no default
                            }

                        }
                    }

                    switch (state.likertAnswerTextDisplay) {
                        case icon.LIKERT_TITLES_ALL:
                            iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL : icon.POSITION_ICON;
                            break;
                        case icon.LIKERT_TITLES_LR:
                            if (i === 0 || i === (answersToAdd - 1)) {
                                iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL : icon.POSITION_ICON;
                            } else {
                                iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL_NO_TITLE : icon.POSITION_ICON_NO_TITLE;
                            }
                            break;
                        case icon.LIKERT_TITLES_LCR:
                            if (i === 0 || i === ((answersToAdd - 1)/2) || i === (answersToAdd - 1)) {
                                iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL : icon.POSITION_ICON;
                            } else {
                                iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL_NO_TITLE : icon.POSITION_ICON_NO_TITLE;
                            }
                            break;
                        case icon.LIKERT_TITLES_HIDDEN:
                            iconAnswerTextDisplay = (state.likertMode === icon.LIKERT_ICON_MODE_FILL) ? icon.POSITION_ICON_FILL_NO_TITLE : icon.POSITION_ICON_NO_TITLE;
                            break;
                        // no default
                    }

                    if (state.likertResponsiveLabels === icon.RESPONSIVE_LABELS_YES_TEXT) {
                        dispatch(addAnswer(groupTemplateMode, {optionText: (i+1).toString(), index: i, metadata: {[icon.ICON_POSITION]: iconAnswerTextDisplay, [icon.ICON_TYPE]: iconType, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.USE_LIKERT_RESPONSIVE_LABEL]: iconResponsiveLabel}}));
                    }
                    else {
                        dispatch(addAnswer(groupTemplateMode, {optionText: (i+1).toString(), index: i, metadata: {[icon.ICON_POSITION]: iconAnswerTextDisplay, [icon.ICON_TYPE]: iconType, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX}}));
                    }
                    break;
                case "squares":

                    // Squares have no titles by default.  To add titles is group level metadata for LCR.

                    if (state.likertResponsiveLabels === icon.RESPONSIVE_LABELS_YES_TEXT) {

                        if (state.likertAnswerTextDisplay === icon.LIKERT_TITLES_HIDDEN) {
                            dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)), index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_SQUARE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.USE_LIKERT_RESPONSIVE_LABEL]: icon.getLabel(answersToAdd, i), [portalShared.DISPLAY_DEF_HIDE_BUTTONGROUP_NAME]: ""}}));
                        } else {
                            dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)), index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_SQUARE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.USE_LIKERT_RESPONSIVE_LABEL]: icon.getLabel(answersToAdd, i)}}));
                        }

                    }
                    else {

                        if (state.likertAnswerTextDisplay === icon.LIKERT_TITLES_HIDDEN) {
                            dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)), index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_SQUARE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.DISPLAY_DEF_HIDE_BUTTONGROUP_NAME]: ""}}));
                        } else {
                            dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)), index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_SQUARE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX}}));
                        }

                    }

                    // this.setMetadata([portalShared.DISPLAY_DEF_RADIO_AS_BUTTON_GROUP], ""); will be called on receive props
                    break;

                case "circles":

                    // Circles have no titles by default.  To add titles is group level metadata for LCR.

                    // if (answersToAdd === 11 && (state.likertMode === icon.LIKERT_ICON_MODE_COLORS || state.likertMode === icon.LIKERT_ICON_MODE_COLORS_ALT)) {
                    //
                    //     if (state.likertResponsiveLabels === icon.RESPONSIVE_LABELS_YES_TEXT)  {
                    //         addAnswer(groupTemplateMode, {optionText: i, index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_CIRCLE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.USE_LIKERT_RESPONSIVE_LABEL]: icon.getLabel(answersToAdd, i)}});
                    //     } else {
                    //         addAnswer(groupTemplateMode, {optionText: i, index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_CIRCLE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE}});
                    //     }
                    //
                    // } else {

                    if (state.likertResponsiveLabels === icon.RESPONSIVE_LABELS_YES_TEXT) {

                        if (state.likertAnswerTextDisplay === icon.LIKERT_TITLES_HIDDEN) {
                            dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)).toString(), index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_CIRCLE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.USE_LIKERT_RESPONSIVE_LABEL]: icon.getLabel(answersToAdd, i), [portalShared.DISPLAY_DEF_HIDE_BUTTONGROUP_NAME]: ""}}));
                        } else {
                            dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)).toString(), index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_CIRCLE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.USE_LIKERT_RESPONSIVE_LABEL]: icon.getLabel(answersToAdd, i)}}));
                        }

                    }
                    else {

                        if (state.likertAnswerTextDisplay === icon.LIKERT_TITLES_HIDDEN) {
                            dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)).toString(), index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_CIRCLE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX, [portalShared.DISPLAY_DEF_HIDE_BUTTONGROUP_NAME]: ""}}));
                        } else {
                            dispatch(addAnswer(groupTemplateMode, {optionText: (i+(answersToAdd === 11 ? 0 : 1)).toString(), index: i, metadata: {[icon.ICON_POSITION]: icon.POSITION_ICON, [icon.ICON_TYPE]: icon.TYPE_CIRCLE, [icon.ICON_TITLES]: state.likertAnswerTextDisplay, [icon.ICON_COLOR]: color.COLOR_DEFAULT_BLUE_HEX}}));
                        }

                    }

                    //}

                    break;
                // no default
            }

        }
    }, [groupTemplateMode, questionDepth, state.likertAddAnswerTemplate, state.likertAddAnswerTemplateFormat, state.likertAnswerTextDisplay, state.likertMode, state.likertResponsiveLabels, dispatch]);

    const onButtonClickRemoveLikertAnswers = useCallback(() => {

        //let updatedTemplate = {...template};
        //let tempMetadata = {...updatedTemplate.metadata};

        // NOTE: GROUP and QUESTION CUSTOM OBJECT Should not be removed when answers are, Only when LIKERT status changes

        // if (template.metadata.hasOwnProperty(portalShared.DISPLAY_DEF_RADIO_AS_BUTTON_GROUP)) {
        //     delete tempMetadata[portalShared.DISPLAY_DEF_RADIO_AS_BUTTON_GROUP];
        // }
        //
        // if (template.metadata.hasOwnProperty(portalShared.DISPLAY_DEF_BUTTON_AS_CIRCLE)) {
        //     delete tempMetadata[portalShared.DISPLAY_DEF_BUTTON_AS_CIRCLE];
        // }

        // Remove all the answers
        let numOfAnswers = answerOptionTemplates.length - 1;

        for (let i = numOfAnswers; i >= 0; i--) {
            removeLikertAnswer(i);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answerOptionTemplates]);

    const removeLikertAnswer = useCallback((index) => {
        dispatch(removeAnswer(groupTemplateMode, index));
    }, [dispatch, groupTemplateMode]);

    const onSelectChange = useCallback((event, fieldName) => {

        let selected = event.target.value;

        let tempLikertMode = "";
        let tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;
        //let tempLikertResponsiveLabels = icon.RESPONSIVE_LABELS_NO_TEXT;

        switch (fieldName) {
            case "likertAddAnswerTemplate":
                setState({[fieldName]: selected, likertAddAnswerTemplateFormat: "radios", likertMode: icon.LIKERT_ICON_MODE_DEFAULT, likertAnswerTextDisplay: icon.LIKERT_TITLES_ALL, likertResponsiveLabels: icon.RESPONSIVE_LABELS_NO_TEXT});
                break;
            case "likertAddAnswerTemplateFormat":

                if (selected === "stars" || selected === "facesA" || selected === "facesD") {
                    tempLikertMode = icon.LIKERT_ICON_MODE_NO_FILL;
                } else {
                    tempLikertMode = icon.LIKERT_ICON_MODE_DEFAULT;
                }

                if (selected === "radios" || selected === "thumbs") {

                    tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;

                } else if (selected === "stars") {

                    if (state.likertAddAnswerTemplate === "4" || state.likertAddAnswerTemplate === "6") {
                        tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;
                    } else {
                        tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;
                    }

                } else if (selected === "facesA" || selected === "facesD") {

                    tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;

                    // } else if (selected === "circles" && state.likertAddAnswerTemplate === "11" && (state.likertMode === icon.LIKERT_ICON_MODE_COLORS || state.likertMode === icon.LIKERT_ICON_MODE_COLORS_ALT)) {
                    //
                    //     tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_HIDDEN;

                } else if (selected === "circles" || selected === "squares") {

                    tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_EMBEDDED;

                } else {
                    tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_HIDDEN;
                }

                setState({[fieldName]: selected, likertMode: tempLikertMode, likertAnswerTextDisplay: tempLikertAnswerTextDisplay, likertResponsiveLabels: icon.RESPONSIVE_LABELS_NO_TEXT});
                break;
            case "likertMode":

                if (state.likertAddAnswerTemplateFormat === "radios" || state.likertAddAnswerTemplateFormat === "thumbs") {

                    tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;

                } else if (state.likertAddAnswerTemplateFormat === "stars") {

                    if (state.likertAddAnswerTemplate === "4" || state.likertAddAnswerTemplate === "6") {
                        tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;
                    } else {
                        tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;
                    }

                } else if (state.likertAddAnswerTemplateFormat === "facesA" || state.likertAddAnswerTemplateFormat === "facesD") {

                    tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_ALL;

                    // } else if (state.likertAddAnswerTemplateFormat === "circles" && state.likertAddAnswerTemplate === "11" && (selected === icon.LIKERT_ICON_MODE_COLORS || selected === icon.LIKERT_ICON_MODE_COLORS_ALT)) {
                    //
                    //     tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_HIDDEN;

                } else if (state.likertAddAnswerTemplateFormat === "circles" || state.likertAddAnswerTemplateFormat === "squares") {

                    tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_EMBEDDED;

                } else {
                    tempLikertAnswerTextDisplay = icon.LIKERT_TITLES_HIDDEN;
                }

                setState({[fieldName]: selected, likertAnswerTextDisplay: tempLikertAnswerTextDisplay, likertResponsiveLabels: icon.RESPONSIVE_LABELS_NO_TEXT});
                break;
            case "likertAnswerTextDisplay":
                setState({[fieldName]: selected, likertResponsiveLabels: icon.RESPONSIVE_LABELS_NO_TEXT});
                break;
            case "likertResponsiveLabels":
                setState({[fieldName]: selected}, () => setResponsiveLabelsInMetadata(selected));
                break;
            default:
                setState({[fieldName]: selected});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.likertAddAnswerTemplate, state.likertAddAnswerTemplateFormat, state.likertMode, state.likertAnswerTextDisplay]);

    const setResponsiveLabelsInMetadata = useCallback((selected) => {

        let clonedTemplate = portalShared.cloneObj(template);
        let tempMetadata = JSON.parse(clonedTemplate.metadata[state.metadataTagCustomTemplate]);
        tempMetadata.likertObj.responsiveLabels = selected;
        clonedTemplate.metadata[state.metadataTagCustomTemplate] = JSON.stringify(tempMetadata);
        templateUpdateFunction(clonedTemplate);
    }, [template, state.metadataTagCustomTemplate, templateUpdateFunction]);

    // const validateAnswers = useCallback((answerOptionTemplatesParam) => {
    //     for (let answerOptionTemplate of answerOptionTemplatesParam) {
    //         // Every answer option must have a name, but cannot exceed 255 characters.
    //         if (answerOptionTemplate.optionText?.length === 0 || answerOptionTemplate.optionText?.length > 255) {
    //             // Fail validation
    //             return false;
    //         }
    //     }
    //     return true;
    // }, []);

    const setMetadata = useCallback((metadataTagParam, metadataValue) => {

        let updatedTemplate = portalShared.cloneObj(template);
        let tempMetadata = {...updatedTemplate.metadata};
        tempMetadata[metadataTagParam] = metadataValue;
        handleMetadataChange(tempMetadata);
    }, [template, handleMetadataChange]);

    const deleteMetadata = useCallback((tempMetadata) => {
        handleMetadataChange(tempMetadata);
    }, [handleMetadataChange]);

    // const deleteSingleMetadata = useCallback((metadataTagParam) => {
    //
    //     let updatedTemplate = portalShared.cloneObj(template);
    //     let tempMetadata = {...updatedTemplate.metadata};
    //
    //     if (tempMetadata.hasOwnProperty(metadataTagParam)) {
    //         delete tempMetadata[metadataTagParam];
    //     }
    //     handleMetadataChange(tempMetadata);
    // }, [template, handleMetadataChange]);

    const onChangeLikertCustomObj = useCallback((event, fieldName, fieldType) => {

        let tempLikertObj =  portalShared.cloneObj(state.likertObj); // Clone state likert object

        let obj = {};
        let objExists = false;

        // Safety check to make sure property exists.  It always should!
        if (template.metadata.hasOwnProperty(customMetadataKey)) {
            obj = JSON.parse(template.metadata[customMetadataKey]);
            objExists = true;
        }

        switch(fieldType) {
            case "text":
            case "select":
                if (fieldName.includes(".")) {
                    let fieldsArray = fieldName.split(".");
                    tempLikertObj[fieldsArray[0]][fieldsArray[1]] = event.target.value;
                    if (objExists) {
                        obj.likertObj[fieldsArray[0]][fieldsArray[1]] = event.target.value;
                    }
                } else {
                    tempLikertObj[fieldName] = `${event.target.value}${fieldName === 'indent' ? 'px' : ''}`;
                    if (objExists) {
                        obj.likertObj[fieldName] = `${event.target.value}${fieldName === 'indent' ? 'px' : ''}`;
                    }
                }

                if ((fieldName === "titleType") && (event.target.value === "none")) {
                    obj.likertObj.titleLeft = "";
                    obj.likertObj.titleCenter = "";
                    obj.likertObj.titleRight = "";

                    tempLikertObj.titleLeft = "";
                    tempLikertObj.titleCenter = "";
                    tempLikertObj.titleRight = "";
                }
                if ((fieldName === "titleType") && (event.target.value === "graybar")) {
                    obj.likertObj.grayBarColorObj.colorType = "select";
                    obj.likertObj.grayBarColorObj.colorIndex = 1;
                    obj.likertObj.grayBarColorObj.colorEntry = portalShared.DEFAULT_GRAYBAR_COLOR;
                    obj.likertObj.grayBarColorObj.color = portalShared.DEFAULT_GRAYBAR_COLOR;
                    obj.likertObj.grayBarColorObj.opacity = 100;
                    obj.likertObj.grayBarColorObj.opacityEntry = "100%";
                    obj.likertObj.grayBarHeight = portalShared.DEFAULT_GRAYBAR_HEIGHT;

                    tempLikertObj.grayBarColorObj.colorType = "select";
                    tempLikertObj.grayBarColorObj.colorIndex = 1;
                    tempLikertObj.grayBarColorObj.colorEntry = portalShared.DEFAULT_GRAYBAR_COLOR;
                    tempLikertObj.grayBarColorObj.color = portalShared.DEFAULT_GRAYBAR_COLOR;
                    tempLikertObj.grayBarColorObj.opacity = 100;
                    tempLikertObj.grayBarColorObj.opacityEntry = "100";
                    tempLikertObj.grayBarHeight = portalShared.DEFAULT_GRAYBAR_HEIGHT;
                }
                break;
            case "checkbox":
                tempLikertObj[fieldName] = event.target.checked;
                if (objExists) {
                    obj.likertObj[fieldName] = event.target.checked
                }
                break;
            case "number":
                tempLikertObj[fieldName] = event.target.value;
                if (objExists) {
                    obj.likertObj[fieldName] = event.target.value;
                }
                break;
            // no default
        }

        // Need to update local state and metadata with the change
        setState({likertObj: tempLikertObj}, () => {setMetadata(customMetadataKey, JSON.stringify(obj))});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.likertObj, template.metadata, customMetadataKey, setMetadata]);

    const answerOptionEditorLikert = useCallback((propsObj) => {

        // if (answerOptionTemplates === undefined) {
        //     return(<div></div>);
        // }

        let format = "default";

        if (state.likertObj.hasOwnProperty("responsiveLabelsCustomTextObj")) {
            if (state.likertObj.responsiveLabelsCustomTextObj !== undefined) {
                if (state.likertObj.responsiveLabelsCustomTextObj.hasOwnProperty("format")) {
                    format = state.likertObj.responsiveLabelsCustomTextObj.format;
                }
            }
        }

        // Allow Thumbs to have the color overridden per answer for example: GREEN thumbs up and RED thumbs down
        let showCustomColor = false;
        if (answerOptionTemplates?.length > 0) {
            let answer0 = answerOptionTemplates[0].metadata[icon.ICON_TYPE];
            let answer1 = answerOptionTemplates[1].metadata[icon.ICON_TYPE];
            if (answer0 === icon.TYPE_THUMBS_UP || answer1 === icon.TYPE_THUMBS_DOWN) {
                showCustomColor = true;
            }
        }

        return (
            <div style={{width: "100%", minHeight: "min-content"}}>

                {answerOptionTemplates?.length > 0 ?
                    <AnswerEditor
                        groupTemplateMode={groupTemplateMode}
                        questionDepth={questionDepth}
                        disableRemoveIcon={true}
                        isLikertAnswer={true}
                        displayAsLikert={displayAsLikert}
                        templateKey={template.templateKey}
                        hasAnswers={answerOptionTemplates > 0}
                        template={template}
                        answerOptionTemplates={answerOptionTemplates}
                        answerEditorSetStateCallback={setStateCallback}
                        setStateCallback={setStateCallback}
                        setMetadataErrorText={setMetadataErrorText}
                        showCustomColor={showCustomColor}
                        createMode={createMode}
                        //groupTemplateMode={groupTemplateMode}
                        // setAnswerMetadataErrorText={setMetadataErrorText}
                    /> : ""
                }

                {/* If there are answers then show the REMOVE ANSWERS button else show ADD ANSWERS component generator*/}

                {answerOptionTemplates?.length > 0 ?
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center"
                    }}>
                        <div style={{alignItems: "left", paddingTop: '14px'}}>

                            <ArgoButtonHook
                                fileName="ArgoLikertAnswerGenerator"
                                fieldName="remove-answers"
                                variant="contained"
                                label={answerOptionTemplates?.length === 1 ? "Remove Answer" : "Remove Answers"}
                                paddingTop="0px"
                                width={140}
                                containedTextColor={'white'}
                                containedBgColor={Colors.blue[500]}
                                containedHoverTextColor={'#010203'}
                                containedHoverBgColor={Colors.blue[500]}
                                buttonPadding={'6px 5px'}
                                onClick={onButtonClickRemoveLikertAnswers}
                            />

                        </div>
                    </div>

                    :

                    <div id="argo-likert-components-container-div" style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: 'center'}}>

                        <ArgoButtonHook
                            fileName="ArgoLikertAnswerGenerator"
                            fieldName="add-answers"
                            variant="contained"
                            label={state.likertAddAnswerTemplate === "1" ? "Add Answer" : "Add Answers"}
                            paddingTop="0px"
                            width={140}
                            containedTextColor={'white'}
                            containedBgColor={Colors.blue[500]}
                            containedHoverTextColor={'#010203'}
                            containedHoverBgColor={Colors.blue[500]}
                            onClick={onButtonClickAddLikertAnswers}
                        />

                        <div style={{alignItems: "left", width: "20px"}}></div>

                        <ArgoTextFieldSelectHook
                            fileName="ArgoLikertAnswerGenerator"
                            fieldName="likertAddAnswerTemplate"
                            label="Answer Count"
                            value={state.likertAddAnswerTemplate}
                            onChange={onSelectChange}
                            width="175px"
                            menuItems={answerTypes.numberOfAnswersList}
                        />

                        <div style={{alignItems: "left", width: "20px"}}></div>

                        {/* ICON TYPES ALLOWED DEPEND ON THE NUMBER OF ANSWERS SELECTED */}

                        <ArgoTextFieldSelectHook
                            fileName="ArgoLikertAnswerGenerator"
                            fieldName="likertAddAnswerTemplateFormat"
                            label="Style"
                            value={state.likertAddAnswerTemplateFormat}
                            onChange={onSelectChange}
                            width="250px"
                            menuItems={answerTypes.buildAddAnswerTemplateMenuItemsArray(state.likertAddAnswerTemplate)}
                        />

                        <div style={{alignItems: "left", width: "20px"}}></div>

                        <ArgoTextFieldSelectHook
                            fileName="ArgoLikertAnswerGenerator"
                            fieldName="likertMode"
                            label="Mode"
                            value={state.likertMode}
                            onChange={onSelectChange}
                            width="250px"
                            valueProperty={"text"}
                            menuItems={answerTypes.getLikertModes(state.likertAddAnswerTemplateFormat, state.likertAddAnswerTemplate)}
                        />

                        <div style={{alignItems: "left", width: "20px"}}></div>

                        <ArgoTextFieldSelectHook
                            fileName="argo-likert-components"
                            fieldName="likertAnswerTextDisplay"
                            label="Answer Text Display"
                            value={state.likertAnswerTextDisplay}
                            onChange={onSelectChange}
                            width="240px"
                            valueProperty={"text"}
                            menuItems={answerTypes.getLikertAnswerTextDisplay(state.likertAddAnswerTemplateFormat, state.likertAddAnswerTemplate, state.likertMode)}
                        />

                        <div style={{alignItems: "left", width: "20px"}}></div>

                        <ArgoTextFieldSelectHook
                            fileName="argo-likert-components"
                            fieldName="likertResponsiveLabels"
                            label="Responsive Labels"
                            value={state.likertResponsiveLabels}
                            onChange={onSelectChange}
                            width="150px"
                            valueProperty={"text"}
                            menuItems={answerTypes.getLikertResponsiveLabels(state.likertAddAnswerTemplateFormat)}
                        />

                    </div>
                }

                <div>

                    <div style={{height: "40px"}}>&nbsp;</div>

                    <div id="argo-likert-responsive-labels-container-div" style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-start",
                        alignItems: "center"
                    }}>
                        <div style={{width: "10px"}}>&nbsp;</div>

                        <div style={{float: "left"}}>
                            {((state.likertResponsiveLabels === icon.RESPONSIVE_LABELS_YES_TEXT) && (answerOptionTemplates.length > 0)) ?
                                <ArgoTextFieldSelectHook
                                    fileName="ArgoLikertAnswerGenerator"
                                    fieldName="responsiveLabelsCustomTextObj"
                                    label="Responsive Labels Display"
                                    value={format}
                                    onChange={(event) => onChangeLikertCustomObj(event, "responsiveLabelsCustomTextObj.format", "select")}
                                    width="150px"
                                    paddingBottom="4px"
                                    menuItems={[
                                        {value: "default", text: "Default"},
                                        {value: "custom", text: "Custom"}
                                    ]}
                                /> : ""}
                        </div>

                        {(answerOptionTemplates.length > 0 && format === 'custom') ?
                            <ArgoTextDisplayCustomHook
                                templateType={templateType}
                                textEntryType={state.textEntryTypeResponsiveLabels}
                                fieldName="responsiveLabelsCustomTextObj.format"
                                fieldTextObjName="responsiveLabelsCustomTextObj"
                                initToObj={state.likertObj.responsiveLabelsCustomTextObj}
                                showDisplay={false}
                                showTitleEntry={false}
                                paddingTop={"0px"}
                                useMetadataPropsObjAltFunction={true}
                                metadataPropsObj={propsObj}
                            /> : ""}
                    </div>
                </div>

            </div>

        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answerOptionTemplates, groupTemplateMode, questionDepth, displayAsLikert, onButtonClickRemoveLikertAnswers, onButtonClickAddLikertAnswers, onSelectChange, onChangeLikertCustomObj, setStateCallback, setMetadataErrorText, state.likertAddAnswerTemplate, state.likertAddAnswerTemplateFormat, state.likertMode, state.likertAnswerTextDisplay, state.likertResponsiveLabels, state.likertObj, state.textEntryTypeResponsiveLabels]);

    const answerTitleEditorLikert = useCallback((propsObj) => {

        let format = "default";

        if (state.likertObj.hasOwnProperty("titlesCustomTextObj")) {
            if (state.likertObj.titlesCustomTextObj !== undefined) {
                if (state.likertObj.titlesCustomTextObj.hasOwnProperty("format")) {
                    format = state.likertObj.titlesCustomTextObj.format;
                }
            }
        }

        return (
            <div>
                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    width: "100%"
                }}>

                    <div style={{width: '20px'}}>&nbsp;</div>

                    <div>
                        <ArgoCheckboxHook
                            fileName="ArgoLikertAnswerGenerator"
                            fieldName="alignAnswersRight"
                            label="Align answers right"
                            onChange={(event) => onChangeLikertCustomObj(event, "alignAnswersRight", "checkbox")}
                            checked={state.likertObj.alignAnswersRight}
                            width="100%"
                            paddingTop="20px"
                        />
                        <span className="notranslate">​</span>
                    </div>

                    <div style={{alignItems: "left", width: "20px"}}></div>

                    {state.likertObj.alignAnswersRight ?
                        <div>
                            <ArgoCheckboxHook
                                fileName="ArgoLikertAnswerGenerator"
                                fieldName="alignQuestionsAndAnswers"
                                label="Align questions with answers"
                                onChange={(event) => onChangeLikertCustomObj(event, "alignQuestionsAndAnswers", "checkbox")}
                                checked={state.likertObj.alignQuestionsAndAnswers}
                                width="100%"
                                paddingTop="20px"
                            />
                            <span className="notranslate">​</span>
                        </div> : ""
                    }

                    <div style={{alignItems: "left", width: "20px"}}></div>

                    {state.likertObj.alignQuestionsAndAnswers ?
                        <ArgoTextFieldHook
                            fileName="ArgoQuestionFormatCustom"
                            fieldName="indent"
                            label="Question Indent (px)"
                            value={parseInt(state.likertObj.indent)}
                            type="number"
                            onChange={(event) => onChangeLikertCustomObj(event, "indent", "text")}
                            width="140px"
                        /> : ""
                    }
                </div>

                <div style={{display: "flex"}}>
                    <div style={{width: '20px'}}>&nbsp;</div>
                    <ArgoTextFieldSelectHook
                        fileName="ArgoLikertAnswerGenerator"
                        fieldName="titleType"
                        label="Titles"
                        value={state.likertObj.titleType}
                        onChange={(event) => onChangeLikertCustomObj(event, "titleType", "select")}
                        width="150px"
                        paddingBottom="4px"
                        menuItems={[
                            {value: "none", text: "None"},
                            {value: "standard", text: "Standard"},
                            {value: "graybar", text: "Graybar"}
                        ]}
                    />

                    {state.likertObj.titleType !== "none" ?
                        <React.Fragment>
                            <div style={{width: '25px'}}>&nbsp;</div>

                            <div>
                                <ArgoTextFieldHook
                                    fileName="ArgoLikertAnswerGenerator"
                                    fieldName="titleLeft"
                                    label="Left"
                                    value={state.likertObj.titleLeft}
                                    onChange={(event) => onChangeLikertCustomObj(event, "titleLeft", "text")}
                                    width="160px"
                                    divContainerHeight="52px"
                                />
                                <span className="notranslate">​</span>
                            </div>

                            <div style={{width: '25px'}}>
                                &nbsp;
                            </div>

                            <div>
                                <ArgoTextFieldHook
                                    fileName="ArgoLikertAnswerGenerator"
                                    fieldName="titleCenter"
                                    label="Center"
                                    value={state.likertObj.titleCenter}
                                    onChange={(event) => onChangeLikertCustomObj(event, "titleCenter", "text")}
                                    width="160px"
                                    divContainerHeight="52px"
                                />
                                <span className="notranslate">​</span>
                            </div>

                            <div style={{width: '25px'}}>&nbsp;</div>

                            <div>
                                <ArgoTextFieldHook
                                    fileName="ArgoLikertAnswerGenerator"
                                    fieldName="titleRight"
                                    label="Right"
                                    value={state.likertObj.titleRight}
                                    onChange={(event) => onChangeLikertCustomObj(event, "titleRight", "text")}
                                    width="160px"
                                    divContainerHeight="52px"
                                />
                                <span className="notranslate">​</span>
                            </div>

                            <div style={{width: '25px'}}> &nbsp;</div>

                            {state.likertObj.titleType === "graybar" ?
                                <ArgoTextFieldHook
                                    fileName="ArgoLikertAnswerGenerator"
                                    fieldName="grayBarHeight"
                                    type="number"
                                    label="Height"
                                    value={state.likertObj.grayBarHeight}
                                    onChange={(event) => onChangeLikertCustomObj(event, "grayBarHeight", "number")}
                                    width={60}
                                />
                                : ""
                            }
                        </React.Fragment> : ""
                    }
                </div>

                <div style={{display: "flex"}}>
                    {state.likertObj.titleType !== "none" ?
                        <React.Fragment>
                            <div style={{width: '20px'}}>&nbsp;</div>

                            <ArgoTextFieldSelectHook
                                fileName="ArgoLikertAnswerGenerator"
                                fieldName="titlesCustomTextObj.format"
                                label="Titles Display"
                                value={format}
                                onChange={(event) => onChangeLikertCustomObj(event, "titlesCustomTextObj.format", "select")}
                                width="150px"
                                paddingBottom="4px"
                                menuItems={[
                                    {value: "default", text: "Default"},
                                    {value: "custom", text: "Custom"}
                                ]}
                            />

                            {(format === 'custom') ?
                                <ArgoTextDisplayCustomHook
                                    templateType={templateType}
                                    textEntryType={state.textEntryTypeLikertTitles}
                                    fieldName="titlesCustomTextObj.format"
                                    fieldTextObjName="titlesCustomTextObj"
                                    initToObj={state.likertObj.titlesCustomTextObj}
                                    showDisplay={false}
                                    showTitleEntry={false}
                                    paddingTop={"0px"}
                                    useMetadataPropsObjAltFunction={true}
                                    metadataPropsObj={propsObj}
                                />
                                :
                                <div></div>
                            }
                        </React.Fragment> : ""
                    }
                </div>

                {(state.likertObj.titleType === "graybar") ?
                    <div id="ArgoLikertAnswerGenerator-graybar-container-div" style={{display: "flex", flexDirection: "row", width: '99%', backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX, paddingLeft: "20px"}}>
                        <ArgoColorInputHook colorLabel="Graybar color override"
                                            opacityFlag={true}
                                            setColor={setGraybarColor}
                                            initToObj={state.likertObj.grayBarColorObj}
                                            largeHexEntry={false}
                                            index={100}/>
                    </div>

                    :
                    <div></div>
                }

                <div id="div-spacer-after-likert-titles2" style={{height: '40px'}}>&nbsp;</div>

            </div>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answerOptionTemplates.length, onChangeLikertCustomObj, state.likertObj, state.textEntryTypeLikertTitles]);

    // updateTemplateForLikert = (aa, bb, cc, dd, ee) => {
    //
    //     // NOTE could be group or question template
    //
    // }
    // onTextChangeForLikert = (aa, bb, cc, dd, ee) => {
    //
    //     // NOTE could be group or question template
    //
    // }

    const setStateCallbackForLikert = useCallback((obj) => {
        let key = Object.keys(obj)[0];
        let valueObj = obj[key].metadataValueObj;

        let likertObj = portalShared.cloneObj(state.likertObj);

        likertObj[key] = valueObj;

        setState({likertObj: likertObj});
    }, [state.likertObj, setState]);


    // setCustomMetadataFunction(fieldTextObjName, mpo.template, mpo.templateUpdateFunction, mpo.setStateCallbackFunction, fieldName, "custom", mpo.metadataTag, valueObj)});
    const setCustomMetadataLayeredForLikert = useCallback((fieldTextObjName, templateParam, templateUpdateFunctionParam, setStateCallbackFunction, fieldName, format, metadataTagParam, valueObj) => {

        let clonedTemplate = portalShared.cloneObj(templateParam);
        //let tempMetadata = {...updatedTemplate.metadata};

        let customTemplateObj = null;
        if (clonedTemplate.metadata.hasOwnProperty(metadataTagParam)) {
            customTemplateObj = JSON.parse(clonedTemplate.metadata[metadataTagParam]);
            customTemplateObj.likertObj[fieldTextObjName] = valueObj;
        }

        clonedTemplate.metadata[metadataTagParam] = JSON.stringify(customTemplateObj);

        if (templateType === TEMPLATE.TYPE.GROUP) {
            clonedTemplate.questionGroupType = groupType.GROUP_TYPE_LIKERT; // ZZZZZ Override to FIX bug created in Class to Hook AI conversion
            dispatch(updateGroupTemplate(clonedTemplate));
        } else {
            dispatch(updateQuestionTemplate(clonedTemplate));
        }
    }, [templateType, dispatch]);

    const setGraybarColor = useCallback((colorObj) => {

        let clonedLikertObj = portalShared.cloneObj(state.likertObj);

        clonedLikertObj.grayBarColorObj = colorObj[Object.keys(colorObj)[0]]; // Get the colorObj

        let customMetadata =  JSON.parse(template.metadata[customMetadataKey]);

        customMetadata.likertObj.grayBarColorObj = clonedLikertObj.grayBarColorObj;

        setState({likertObj: clonedLikertObj}, () => setMetadata(customMetadataKey, JSON.stringify(customMetadata)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.likertObj, template.metadata, customMetadataKey, setMetadata]);

    // Build props object used by nested custom text components
    const propsObj = {
        fileName: "ArgoLikertAnswerGenerator",
        templateType: templateType,
        template: template,
        metadata: template?.metadata,
        metadataTag: state.metadataTagCustomTemplate,
        templateUpdateFunction: templateUpdateFunction,
        setStateCallbackFunction: setStateCallbackForLikert,
        setCustomMetadataFunction: metadata.setCustomMetadata,
        setCustomMetadataFunctionAlt: setCustomMetadataLayeredForLikert,
        depth: 0
    };

    // Final render logic (converted from class render)
    if (groupTemplateMode) {

        if (isLikert) {
            return (
                <div>
                    <div>
                        {answerTitleEditorLikert(propsObj)}
                    </div>
                    <div>
                        {answerOptionEditorLikert(propsObj)}
                    </div>
                </div>
            );
        }
        return null;
    }

    return (
        <div>
            {fileName === "QuestionEditor" ?
                <div>
                    {answerOptionEditorLikert(propsObj)}
                </div>
                :
                <div>
                    {(isLikert ? answerTitleEditorLikert(propsObj) : "")}
                </div>
            }
        </div>
    );
};

ArgoLikertAnswerGeneratorHook.defaultProps = {
    groupTemplateMode: false,
    isLikert: false,
    questionDepth: 1
};

ArgoLikertAnswerGeneratorHook.propTypes = {
    fileName: PropTypes.string,
    templateType: PropTypes.string,
    groupTemplateMode: PropTypes.bool,
    componentMode: PropTypes.string,
    template: PropTypes.object,
    templateUpdateFunction: PropTypes.func,
    displayAsLikert: PropTypes.bool,
    isLikert: PropTypes.bool,
    answerOptionTemplates: PropTypes.array,
    answerErrorCallback: PropTypes.func,
    questionDepth: PropTypes.number,
    setMetadataErrorText: PropTypes.func,
    setStateCallback: PropTypes.func,
    customMetadataKey: PropTypes.string,
    propsObj: PropTypes.object
};

export default ArgoLikertAnswerGeneratorHook;
